import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Divider,
    Alert,
    Tab,
    Tabs,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Slider,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Snackbar,
    CircularProgress,
    Card,
    CardContent
} from '@mui/material';
import {
    Settings as SettingsIcon,
    Security as SecurityIcon,
    Storage as StorageIcon,
    Notifications as NotificationsIcon,
    Email as EmailIcon,
    CloudUpload as CloudIcon,
    Code as APIIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Info as InfoIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import authService from '../services/auth.service';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

interface SystemSettings {
    general: {
        systemName: string;
        organizationName: string;
        timezone: string;
        dateFormat: string;
        language: string;
        maintenanceMode: boolean;
    };
    security: {
        sessionTimeout: number;
        passwordMinLength: number;
        passwordRequireSpecialChar: boolean;
        passwordRequireNumbers: boolean;
        passwordExpireDays: number;
        maxLoginAttempts: number;
        enableTwoFactor: boolean;
        allowedFileTypes: string[];
        maxFileSize: number;
    };
    storage: {
        storageType: 'local' | 'cloud';
        localPath: string;
        cloudProvider: string;
        cloudBucket: string;
        retentionDays: number;
        enableCompression: boolean;
        compressionLevel: number;
    };
    processing: {
        enableOCR: boolean;
        ocrLanguages: string[];
        enableTextExtraction: boolean;
        enableThumbnails: boolean;
        thumbnailSize: number;
        maxConcurrentJobs: number;
        jobTimeout: number;
    };
    notifications: {
        enableEmail: boolean;
        smtpHost: string;
        smtpPort: number;
        smtpUser: string;
        smtpSecure: boolean;
        emailFrom: string;
        enableWebhooks: boolean;
        webhookUrl: string;
    };
    api: {
        rateLimit: number;
        rateLimitWindow: number;
        enableCORS: boolean;
        corsOrigins: string[];
        apiKeyExpiration: number;
        enableGraphQL: boolean;
        enableSwagger: boolean;
    };
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const Settings: React.FC = () => {
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [originalSettings, setOriginalSettings] = useState<SystemSettings | null>(null);
    const [modifiedSections, setModifiedSections] = useState<Set<string>>(new Set());
    const [settings, setSettings] = useState<SystemSettings>({
        general: {
            systemName: 'Depot Bluelot',
            organizationName: 'Bluelot Inc.',
            timezone: 'America/New_York',
            dateFormat: 'MM/DD/YYYY',
            language: 'en',
            maintenanceMode: false
        },
        security: {
            sessionTimeout: 30,
            passwordMinLength: 8,
            passwordRequireSpecialChar: true,
            passwordRequireNumbers: true,
            passwordExpireDays: 90,
            maxLoginAttempts: 5,
            enableTwoFactor: false,
            allowedFileTypes: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'png', 'txt'],
            maxFileSize: 50
        },
        storage: {
            storageType: 'local',
            localPath: './uploads',
            cloudProvider: 'aws',
            cloudBucket: '',
            retentionDays: 365,
            enableCompression: true,
            compressionLevel: 6
        },
        processing: {
            enableOCR: true,
            ocrLanguages: ['eng', 'spa'],
            enableTextExtraction: true,
            enableThumbnails: true,
            thumbnailSize: 200,
            maxConcurrentJobs: 5,
            jobTimeout: 300
        },
        notifications: {
            enableEmail: false,
            smtpHost: '',
            smtpPort: 587,
            smtpUser: '',
            smtpSecure: true,
            emailFrom: '',
            enableWebhooks: false,
            webhookUrl: ''
        },
        api: {
            rateLimit: 100,
            rateLimitWindow: 60,
            enableCORS: true,
            corsOrigins: ['http://localhost:3000'],
            apiKeyExpiration: 365,
            enableGraphQL: false,
            enableSwagger: true
        }
    });
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [newFileType, setNewFileType] = useState('');
    const [newCorsOrigin, setNewCorsOrigin] = useState('');
    const [testEmailDialog, setTestEmailDialog] = useState(false);
    const [testEmail, setTestEmail] = useState('');

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const api = authService.getApiInstance();
            const response = await api.get('/settings');
            setSettings(response.data);
            setOriginalSettings(response.data);
            setModifiedSections(new Set());
        } catch (error) {
            console.error('Failed to load settings:', error);
            showSnackbar('Failed to load settings', 'error');
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        try {
            setSaving(true);
            const api = authService.getApiInstance();
            
            // Build payload with only modified sections
            const payload: any = {};
            Array.from(modifiedSections).forEach(section => {
                if (settings[section as keyof SystemSettings]) {
                    payload[section] = settings[section as keyof SystemSettings];
                }
            });
            
            // If nothing was modified, just return
            if (Object.keys(payload).length === 0) {
                showSnackbar('No changes to save', 'info');
                setSaving(false);
                return;
            }
            
            const response = await api.patch('/settings', payload);
            setSettings(response.data.settings);
            setOriginalSettings(response.data.settings);
            setModifiedSections(new Set());
            showSnackbar('Settings saved successfully', 'success');
        } catch (error: any) {
            console.error('Failed to save settings:', error);
            showSnackbar(error.response?.data?.error || 'Failed to save settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (section: keyof SystemSettings, field: string, value: any) => {
        setSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
        
        // Track which section was modified
        setModifiedSections(prev => new Set(Array.from(prev).concat(section)));
    };

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
        setSnackbar({ open: true, message, severity });
    };

    const addFileType = () => {
        if (newFileType && !settings.security.allowedFileTypes.includes(newFileType)) {
            handleChange('security', 'allowedFileTypes', [...settings.security.allowedFileTypes, newFileType]);
            setNewFileType('');
        }
    };

    const removeFileType = (fileType: string) => {
        handleChange('security', 'allowedFileTypes', settings.security.allowedFileTypes.filter(ft => ft !== fileType));
    };

    const addCorsOrigin = () => {
        if (newCorsOrigin && !settings.api.corsOrigins.includes(newCorsOrigin)) {
            handleChange('api', 'corsOrigins', [...settings.api.corsOrigins, newCorsOrigin]);
            setNewCorsOrigin('');
        }
    };

    const removeCorsOrigin = (origin: string) => {
        handleChange('api', 'corsOrigins', settings.api.corsOrigins.filter(o => o !== origin));
    };

    const sendTestEmail = async () => {
        try {
            const api = authService.getApiInstance();
            await api.post('/settings/test-email', { recipient: testEmail });
            showSnackbar('Test email sent successfully', 'success');
            setTestEmailDialog(false);
            setTestEmail('');
        } catch (error: any) {
            showSnackbar(error.response?.data?.error || 'Failed to send test email', 'error');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <div>
                    <Typography variant="h4" gutterBottom>
                        System Settings
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Configure system preferences and options
                    </Typography>
                </div>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadSettings}
                    >
                        Reset
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={saveSettings}
                        disabled={saving}
                    >
                        Save Changes
                    </Button>
                </Box>
            </Box>

            <Paper>
                <Tabs
                    value={tabValue}
                    onChange={(e, newValue) => setTabValue(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab icon={<SettingsIcon />} label="General" />
                    <Tab icon={<SecurityIcon />} label="Security" />
                    <Tab icon={<StorageIcon />} label="Storage" />
                    <Tab icon={<SettingsIcon />} label="Processing" />
                    <Tab icon={<NotificationsIcon />} label="Notifications" />
                    <Tab icon={<APIIcon />} label="API" />
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    {/* General Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <TextField
                                fullWidth
                                label="System Name"
                                value={settings.general.systemName}
                                onChange={(e) => handleChange('general', 'systemName', e.target.value)}
                            />
                        </div>
                        <div>
                            <TextField
                                fullWidth
                                label="Organization Name"
                                value={settings.general.organizationName}
                                onChange={(e) => handleChange('general', 'organizationName', e.target.value)}
                            />
                        </div>
                        <div>
                            <FormControl fullWidth>
                                <InputLabel>Timezone</InputLabel>
                                <Select
                                    value={settings.general.timezone}
                                    onChange={(e) => handleChange('general', 'timezone', e.target.value)}
                                    label="Timezone"
                                >
                                    <MenuItem value="America/New_York">Eastern Time</MenuItem>
                                    <MenuItem value="America/Chicago">Central Time</MenuItem>
                                    <MenuItem value="America/Denver">Mountain Time</MenuItem>
                                    <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                                    <MenuItem value="UTC">UTC</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <div>
                            <FormControl fullWidth>
                                <InputLabel>Date Format</InputLabel>
                                <Select
                                    value={settings.general.dateFormat}
                                    onChange={(e) => handleChange('general', 'dateFormat', e.target.value)}
                                    label="Date Format"
                                >
                                    <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                                    <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                                    <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <div>
                            <FormControl fullWidth>
                                <InputLabel>Language</InputLabel>
                                <Select
                                    value={settings.general.language}
                                    onChange={(e) => handleChange('general', 'language', e.target.value)}
                                    label="Language"
                                >
                                    <MenuItem value="en">English</MenuItem>
                                    <MenuItem value="es">Spanish</MenuItem>
                                    <MenuItem value="fr">French</MenuItem>
                                    <MenuItem value="de">German</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <div className="col-span-full">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.general.maintenanceMode}
                                        onChange={(e) => handleChange('general', 'maintenanceMode', e.target.checked)}
                                    />
                                }
                                label="Maintenance Mode"
                            />
                            {settings.general.maintenanceMode && (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    System is in maintenance mode. Users cannot access the system.
                                </Alert>
                            )}
                        </div>
                    </div>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    {/* Security Settings */}
                    <div className="space-y-6">
                        <div>
                            <Typography variant="h6" gutterBottom>
                                Session Settings
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Session Timeout (minutes)"
                                    value={settings.security.sessionTimeout}
                                    onChange={(e) => handleChange('security', 'sessionTimeout', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 5, max: 480 } }}
                                />
                            </div>
                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Max Login Attempts"
                                    value={settings.security.maxLoginAttempts}
                                    onChange={(e) => handleChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 3, max: 10 } }}
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Password Policy
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Minimum Password Length"
                                    value={settings.security.passwordMinLength}
                                    onChange={(e) => handleChange('security', 'passwordMinLength', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 6, max: 32 } }}
                                />
                            </div>
                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Password Expire Days"
                                    value={settings.security.passwordExpireDays}
                                    onChange={(e) => handleChange('security', 'passwordExpireDays', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 0, max: 365 } }}
                                    helperText="0 = Never expire"
                                />
                            </div>
                        </div>
                        <div className="space-y-4">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.security.passwordRequireSpecialChar}
                                        onChange={(e) => handleChange('security', 'passwordRequireSpecialChar', e.target.checked)}
                                    />
                                }
                                label="Require Special Characters"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.security.passwordRequireNumbers}
                                        onChange={(e) => handleChange('security', 'passwordRequireNumbers', e.target.checked)}
                                    />
                                }
                                label="Require Numbers"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.security.enableTwoFactor}
                                        onChange={(e) => handleChange('security', 'enableTwoFactor', e.target.checked)}
                                    />
                                }
                                label="Enable Two-Factor Authentication"
                            />
                        </div>

                        <div>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                File Upload Security
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Max File Size (MB)"
                                    value={settings.security.maxFileSize}
                                    onChange={(e) => handleChange('security', 'maxFileSize', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 1, max: 1000 } }}
                                />
                            </div>
                        </div>
                        <div>
                            <Typography variant="subtitle2" gutterBottom>
                                Allowed File Types
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                {settings.security.allowedFileTypes.map((fileType) => (
                                    <Chip
                                        key={fileType}
                                        label={fileType}
                                        onDelete={() => removeFileType(fileType)}
                                    />
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    size="small"
                                    placeholder="Add file type (e.g., csv)"
                                    value={newFileType}
                                    onChange={(e) => setNewFileType(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && addFileType()}
                                />
                                <Button variant="outlined" onClick={addFileType}>
                                    Add
                                </Button>
                            </Box>
                        </div>
                    </div>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    {/* Storage Settings */}
                    <div className="space-y-6">
                        <div>
                            <FormControl>
                                <Typography variant="subtitle2" gutterBottom>
                                    Storage Type
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant={settings.storage.storageType === 'local' ? 'contained' : 'outlined'}
                                        onClick={() => handleChange('storage', 'storageType', 'local')}
                                    >
                                        Local Storage
                                    </Button>
                                    <Button
                                        variant={settings.storage.storageType === 'cloud' ? 'contained' : 'outlined'}
                                        onClick={() => handleChange('storage', 'storageType', 'cloud')}
                                    >
                                        Cloud Storage
                                    </Button>
                                </Box>
                            </FormControl>
                        </div>

                        {settings.storage.storageType === 'local' ? (
                            <div>
                                <TextField
                                    fullWidth
                                    label="Local Storage Path"
                                    value={settings.storage.localPath}
                                    onChange={(e) => handleChange('storage', 'localPath', e.target.value)}
                                    helperText="Path where uploaded files will be stored"
                                />
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <FormControl fullWidth>
                                        <InputLabel>Cloud Provider</InputLabel>
                                        <Select
                                            value={settings.storage.cloudProvider}
                                            onChange={(e) => handleChange('storage', 'cloudProvider', e.target.value)}
                                            label="Cloud Provider"
                                        >
                                            <MenuItem value="aws">Amazon S3</MenuItem>
                                            <MenuItem value="azure">Azure Blob Storage</MenuItem>
                                            <MenuItem value="gcs">Google Cloud Storage</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>
                                <div>
                                    <TextField
                                        fullWidth
                                        label="Bucket Name"
                                        value={settings.storage.cloudBucket}
                                        onChange={(e) => handleChange('storage', 'cloudBucket', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <Divider sx={{ my: 2 }} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Retention Days"
                                    value={settings.storage.retentionDays}
                                    onChange={(e) => handleChange('storage', 'retentionDays', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 0, max: 3650 } }}
                                    helperText="0 = Keep forever"
                                />
                            </div>
                        </div>

                        <div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.storage.enableCompression}
                                        onChange={(e) => handleChange('storage', 'enableCompression', e.target.checked)}
                                    />
                                }
                                label="Enable File Compression"
                            />
                        </div>

                        {settings.storage.enableCompression && (
                            <div>
                                <Typography gutterBottom>
                                    Compression Level: {settings.storage.compressionLevel}
                                </Typography>
                                <Slider
                                    value={settings.storage.compressionLevel}
                                    onChange={(e, value) => handleChange('storage', 'compressionLevel', value)}
                                    min={1}
                                    max={9}
                                    marks
                                    valueLabelDisplay="auto"
                                />
                                <Typography variant="body2" color="text.secondary">
                                    1 = Fast, 9 = Best compression
                                </Typography>
                            </div>
                        )}
                    </div>
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                    {/* Processing Settings */}
                    <div className="space-y-6">
                        <div>
                            <Typography variant="h6" gutterBottom>
                                Document Processing
                            </Typography>
                        </div>
                        
                        <div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.processing.enableOCR}
                                        onChange={(e) => handleChange('processing', 'enableOCR', e.target.checked)}
                                    />
                                }
                                label="Enable OCR (Optical Character Recognition)"
                            />
                        </div>

                        {settings.processing.enableOCR && (
                            <div>
                                <Typography variant="subtitle2" gutterBottom>
                                    OCR Languages
                                </Typography>
                                <FormControl fullWidth>
                                    <Select
                                        multiple
                                        value={settings.processing.ocrLanguages}
                                        onChange={(e) => handleChange('processing', 'ocrLanguages', e.target.value)}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {(selected as string[]).map((value) => (
                                                    <Chip key={value} label={value} size="small" />
                                                ))}
                                            </Box>
                                        )}
                                    >
                                        <MenuItem value="eng">English</MenuItem>
                                        <MenuItem value="spa">Spanish</MenuItem>
                                        <MenuItem value="fra">French</MenuItem>
                                        <MenuItem value="deu">German</MenuItem>
                                        <MenuItem value="chi_sim">Chinese (Simplified)</MenuItem>
                                        <MenuItem value="jpn">Japanese</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        )}

                        <div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.processing.enableTextExtraction}
                                        onChange={(e) => handleChange('processing', 'enableTextExtraction', e.target.checked)}
                                    />
                                }
                                label="Enable Text Extraction"
                            />
                        </div>

                        <div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.processing.enableThumbnails}
                                        onChange={(e) => handleChange('processing', 'enableThumbnails', e.target.checked)}
                                    />
                                }
                                label="Generate Thumbnails"
                            />
                        </div>

                        {settings.processing.enableThumbnails && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Thumbnail Size (px)"
                                        value={settings.processing.thumbnailSize}
                                        onChange={(e) => handleChange('processing', 'thumbnailSize', parseInt(e.target.value))}
                                        InputProps={{ inputProps: { min: 100, max: 500 } }}
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Processing Queue
                            </Typography>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Max Concurrent Jobs"
                                    value={settings.processing.maxConcurrentJobs}
                                    onChange={(e) => handleChange('processing', 'maxConcurrentJobs', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 1, max: 20 } }}
                                />
                            </div>

                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Job Timeout (seconds)"
                                    value={settings.processing.jobTimeout}
                                    onChange={(e) => handleChange('processing', 'jobTimeout', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 60, max: 3600 } }}
                                />
                            </div>
                        </div>
                    </div>
                </TabPanel>

                <TabPanel value={tabValue} index={4}>
                    {/* Notification Settings */}
                    <div className="space-y-6">
                        <div>
                            <Typography variant="h6" gutterBottom>
                                Email Notifications
                            </Typography>
                        </div>

                        <div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.notifications.enableEmail}
                                        onChange={(e) => handleChange('notifications', 'enableEmail', e.target.checked)}
                                    />
                                }
                                label="Enable Email Notifications"
                            />
                        </div>

                        {settings.notifications.enableEmail && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <TextField
                                            fullWidth
                                            label="SMTP Host"
                                            value={settings.notifications.smtpHost}
                                            onChange={(e) => handleChange('notifications', 'smtpHost', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="SMTP Port"
                                            value={settings.notifications.smtpPort}
                                            onChange={(e) => handleChange('notifications', 'smtpPort', parseInt(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <TextField
                                            fullWidth
                                            label="SMTP Username"
                                            value={settings.notifications.smtpUser}
                                            onChange={(e) => handleChange('notifications', 'smtpUser', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <TextField
                                            fullWidth
                                            label="From Email"
                                            type="email"
                                            value={settings.notifications.emailFrom}
                                            onChange={(e) => handleChange('notifications', 'emailFrom', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.notifications.smtpSecure}
                                                onChange={(e) => handleChange('notifications', 'smtpSecure', e.target.checked)}
                                            />
                                        }
                                        label="Use Secure Connection (TLS/SSL)"
                                    />
                                </div>
                                <div>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setTestEmailDialog(true)}
                                    >
                                        Send Test Email
                                    </Button>
                                </div>
                            </>
                        )}

                        <div>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Webhooks
                            </Typography>
                        </div>

                        <div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.notifications.enableWebhooks}
                                        onChange={(e) => handleChange('notifications', 'enableWebhooks', e.target.checked)}
                                    />
                                }
                                label="Enable Webhooks"
                            />
                        </div>

                        {settings.notifications.enableWebhooks && (
                            <div>
                                <TextField
                                    fullWidth
                                    label="Webhook URL"
                                    value={settings.notifications.webhookUrl}
                                    onChange={(e) => handleChange('notifications', 'webhookUrl', e.target.value)}
                                    helperText="POST requests will be sent to this URL for events"
                                />
                            </div>
                        )}
                    </div>
                </TabPanel>

                <TabPanel value={tabValue} index={5}>
                    {/* API Settings */}
                    <div className="space-y-6">
                        <div>
                            <Typography variant="h6" gutterBottom>
                                Rate Limiting
                            </Typography>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Rate Limit (requests)"
                                    value={settings.api.rateLimit}
                                    onChange={(e) => handleChange('api', 'rateLimit', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 10, max: 1000 } }}
                                />
                            </div>

                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Rate Limit Window (seconds)"
                                    value={settings.api.rateLimitWindow}
                                    onChange={(e) => handleChange('api', 'rateLimitWindow', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 1, max: 3600 } }}
                                />
                            </div>
                        </div>

                        <div>
                            <Alert severity="info">
                                {settings.api.rateLimit} requests per {settings.api.rateLimitWindow} seconds
                            </Alert>
                        </div>

                        <div>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                CORS Settings
                            </Typography>
                        </div>

                        <div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.api.enableCORS}
                                        onChange={(e) => handleChange('api', 'enableCORS', e.target.checked)}
                                    />
                                }
                                label="Enable CORS"
                            />
                        </div>

                        {settings.api.enableCORS && (
                            <div>
                                <Typography variant="subtitle2" gutterBottom>
                                    Allowed Origins
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                    {settings.api.corsOrigins.map((origin) => (
                                        <Chip
                                            key={origin}
                                            label={origin}
                                            onDelete={() => removeCorsOrigin(origin)}
                                        />
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        size="small"
                                        placeholder="Add origin (e.g., https://example.com)"
                                        value={newCorsOrigin}
                                        onChange={(e) => setNewCorsOrigin(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && addCorsOrigin()}
                                        fullWidth
                                    />
                                    <Button variant="outlined" onClick={addCorsOrigin}>
                                        Add
                                    </Button>
                                </Box>
                            </div>
                        )}

                        <div>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                API Features
                            </Typography>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="API Key Expiration (days)"
                                    value={settings.api.apiKeyExpiration}
                                    onChange={(e) => handleChange('api', 'apiKeyExpiration', parseInt(e.target.value))}
                                    InputProps={{ inputProps: { min: 0, max: 365 } }}
                                    helperText="0 = Never expire"
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.api.enableGraphQL}
                                        onChange={(e) => handleChange('api', 'enableGraphQL', e.target.checked)}
                                    />
                                }
                                label="Enable GraphQL API"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.api.enableSwagger}
                                        onChange={(e) => handleChange('api', 'enableSwagger', e.target.checked)}
                                    />
                                }
                                label="Enable Swagger Documentation"
                            />
                        </div>
                    </div>
                </TabPanel>
            </Paper>

            {/* Test Email Dialog */}
            <Dialog open={testEmailDialog} onClose={() => setTestEmailDialog(false)}>
                <DialogTitle>Send Test Email</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Recipient Email"
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestEmailDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={sendTestEmail}>
                        Send
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Settings;